import React from "react";
import Link from "next/link";
import Icon from "../components/Icon";
import renderIcon from "../../../components/renderIcon";
import { applyLang } from "../../../i18n";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { sharedStyles, SectionHead } from "./_shared";
import { resolveImage } from "../utils";
import { useEvents } from "../../../services/EventService";

const styles = `
.t26-events__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 420px));
  grid-auto-rows: 1fr;
  gap: clamp(16px, 2vw, 24px);
  justify-content: center;
  max-width: 1100px;
  margin: 0 auto;
}
@media (max-width: 560px) {
  .t26-events__grid { grid-template-columns: minmax(0, 420px); grid-auto-rows: auto; }
}

.t26-event-card {
  display: flex; flex-direction: column;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
}
.t26-event-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
  border-color: color-mix(in oklab, var(--primary) 35%, var(--line));
}

.t26-event-card__imgwrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: color-mix(in oklab, var(--primary) 8%, var(--bg-2));
  overflow: hidden;
}
.t26-event-card__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover;
  transition: transform .4s ease;
}
.t26-event-card:hover .t26-event-card__img { transform: scale(1.04); }
.t26-event-card__img-placeholder {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 8%, var(--bg-2));
}

/* Badge date en coin */
.t26-event-card__datebox {
  position: absolute;
  top: 12px;
  inset-inline-start: 12px;
  background: var(--surface);
  border-radius: var(--r-md);
  padding: 8px 10px 6px;
  min-width: 54px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,.12);
  line-height: 1;
}
.t26-event-card__datebox__day {
  display: block;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
}
.t26-event-card__datebox__month {
  display: block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--primary);
  margin-top: 2px;
}

/* Pill "En cours" */
.t26-event-card__live {
  position: absolute;
  top: 12px;
  inset-inline-end: 12px;
  background: #ef4444;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
  padding: 5px 10px;
  border-radius: var(--r-pill);
  box-shadow: 0 4px 10px rgba(239,68,68,.4);
}
.t26-event-card__live::before {
  content: "";
  display: inline-block;
  width: 6px; height: 6px;
  background: #fff;
  border-radius: 50%;
  margin-inline-end: 6px;
  animation: t26-event-pulse 1.6s infinite;
  vertical-align: middle;
}
@keyframes t26-event-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .3; }
}

.t26-event-card__body {
  padding: 16px 18px 18px;
  display: flex; flex-direction: column; gap: 10px;
  flex: 1;
}
.t26-event-card__name {
  font-family: var(--font-display);
  font-size: 18px; font-weight: 700;
  line-height: 1.25;
  color: var(--ink);
  margin: 0;
  text-wrap: balance;
}
.t26-event-card__meta {
  display: flex; flex-direction: column; gap: 5px;
  font-size: 13px;
  color: var(--ink-2);
}
.t26-event-card__meta-row {
  display: flex; align-items: center; gap: 8px;
  line-height: 1.35;
}
.t26-event-card__meta-row svg {
  flex-shrink: 0;
  color: var(--primary);
}
.t26-event-card__desc {
  font-size: 13px;
  color: var(--ink-3);
  line-height: 1.5;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Pills "liens externes" (réservations, plus d'infos, etc.) */
.t26-event-card__links {
  display: flex; flex-wrap: wrap; gap: 6px;
  margin-top: 2px;
}
.t26-event-card__link {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 12px; font-weight: 600;
  padding: 5px 10px;
  border-radius: var(--r-pill);
  background: color-mix(in oklab, var(--primary) 10%, var(--surface));
  color: var(--primary);
  text-decoration: none;
  transition: background .15s ease, transform .15s ease;
}
.t26-event-card__link:hover {
  background: color-mix(in oklab, var(--primary) 20%, var(--surface));
  transform: translateY(-1px);
}

.t26-event-card__more {
  margin-top: auto;
  padding-top: 10px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: var(--primary);
}
.t26-event-card:hover .t26-event-card__more { text-decoration: underline; }
`;

// Parse une date en ms. Null si invalide.
function toMs(v) {
    if (!v) return null;
    const ms = typeof v === "number" ? v : Date.parse(v);
    return isNaN(ms) ? null : ms;
}

// Début du jour courant (00:00 local) — tolérant pour afficher un event
// qui finit "aujourd'hui" même si sa dateFin est en début de matinée.
function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

function formatDay(ms) {
    return new Date(ms).getDate();
}

function formatMonth(ms, locale) {
    return new Date(ms)
        .toLocaleDateString(locale || "fr", { month: "short" })
        .replace(".", "")
        .toUpperCase();
}

// Libellé de période propre, sans "00:00" bidon :
//  - Un seul jour avec heure : "samedi 12 avril · 20:30"
//  - Un seul jour sans heure : "samedi 12 avril"
//  - Plusieurs jours : "12 → 15 avril"
function formatPeriod(startMs, endMs, locale) {
    if (!startMs) return null;
    const start = new Date(startMs);
    const opts = { weekday: "long", day: "numeric", month: "long" };
    const loc = locale || "fr";

    const timeOpts = { hour: "2-digit", minute: "2-digit" };
    const hasTime = start.getHours() !== 0 || start.getMinutes() !== 0;
    const timeStr = hasTime ? start.toLocaleTimeString(loc, timeOpts) : null;

    if (!endMs || Math.abs(endMs - startMs) < 24 * 3600 * 1000) {
        const dateStr = start.toLocaleDateString(loc, opts);
        return timeStr ? `${dateStr} · ${timeStr}` : dateStr;
    }

    const end = new Date(endMs);
    const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    if (sameMonth) {
        const monthYear = end.toLocaleDateString(loc, { month: "long", year: "numeric" });
        return `${start.getDate()} → ${end.getDate()} ${monthYear}`;
    }
    const startStr = start.toLocaleDateString(loc, { day: "numeric", month: "short" });
    const endStr = end.toLocaleDateString(loc, { day: "numeric", month: "long", year: "numeric" });
    return `${startStr} → ${endStr}`;
}

/**
 * EventsPreview : grille des événements en cours / à venir.
 *
 * Règles (alignées sur Travel/EventsStrip) :
 *   - On filtre `active !== false && !hidden`
 *   - On exclut tout événement dont la date de fin (ou date de début si pas
 *     de date de fin) est antérieure au début de la journée courante.
 *   - Tri par date de début ascendante. Limite par défaut : 3.
 *   - Si un événement est en cours (start ≤ now ≤ end), badge "En cours".
 *   - Aucun "00:00" bidon : si pas d'heure sur la date, on n'affiche que la date.
 */
export default function EventsPreview({ context, limit = 3, eyebrow, title, subtitle }) {
    const { org, lang, langValue } = context;
    // Événements fetchés CÔTÉ CLIENT via /v1/events (modèle galeries/actualités).
    const { events } = useEvents(context);
    if (!Array.isArray(events) || events.length === 0) return null;

    const todayStart = startOfToday();
    const nowMs = Date.now();

    const upcoming = (events || [])
        .filter(e => e && e.active !== false && !e.hidden)
        .map(e => {
            const startMs = toMs(e.dateEvent || e.date_start || e.date);
            const endMs = toMs(e.dateFin || e.date_end) || startMs;
            return { e, startMs, endMs };
        })
        .filter(({ startMs, endMs }) => {
            if (!startMs) return false;
            const effectiveEnd = endMs || startMs;
            return effectiveEnd >= todayStart;
        })
        .sort((a, b) => (a.startMs || 0) - (b.startMs || 0))
        .slice(0, limit);

    if (upcoming.length === 0) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + styles }} />
            <section className="t26-section">
                <SectionHead
                    eyebrow={eyebrow || t`À venir`}
                    title={title || t`Nos événements`}
                    subtitle={subtitle || t`Rejoignez la communauté lors de nos prochains rendez-vous`}
                />

                <div className="t26-events__grid">
                    {upcoming.map(({ e: event, startMs, endMs }) => {
                        const localized = applyLang(event, lang);
                        const imgSrc = event.image ? resolveImage(event.image) : null;
                        const href = `${org.BASE_WEB_URL}/evenement/${event.urlSlug || event.id || event._id}`;
                        const isMulti = !!event.isMultiSession;

                        const isLive = !isMulti && startMs && startMs <= nowMs && (endMs || startMs) >= nowMs;
                        const periodStr = isMulti ? null : formatPeriod(startMs, endMs, langValue);
                        const address = localized?.address?.address || localized.adresse;
                        // Liens externes (réservations, plus d'infos...). Chaque link
                        // a ses propres fragments multilangues (link.fr.name, ...).
                        const links = isMulti
                            ? []
                            : (Array.isArray(event.links)
                                ? event.links.filter(l => l?.url).map(l => applyLang(l, lang))
                                : []);

                        return (
                            <Link key={event.id || event._id} href={href} legacyBehavior>
                                <a className="t26-event-card">
                                    <span style={{display:"block"}} className="t26-event-card__imgwrap">
                                        {imgSrc ? (
                                            <img
                                                src={imgSrc}
                                                alt={localized.name || localized.title || ""}
                                                className="t26-event-card__img"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <span style={{display:"block"}} className="t26-event-card__img-placeholder">
                                                {renderIcon({ name: "IconCalendarEvent", source: "tabler" }, 48)}
                                            </span>
                                        )}

                                        {!isMulti && startMs && (
                                            <span style={{display:"block"}} className="t26-event-card__datebox">
                                                <span className="t26-event-card__datebox__day">{formatDay(startMs)}</span>
                                                <span className="t26-event-card__datebox__month">{formatMonth(startMs, langValue)}</span>
                                            </span>
                                        )}

                                        {isLive && (
                                            <span className="t26-event-card__live">
                                                <Trans>En cours</Trans>
                                            </span>
                                        )}
                                    </span>

                                    <span style={{display:"block"}} className="t26-event-card__body">
                                        <h3 className="t26-event-card__name">{localized.name || localized.title}</h3>

                                        <span style={{display:"block"}} className="t26-event-card__meta">
                                            {isMulti && (
                                                <span className="t26-event-card__meta-row">
                                                    {renderIcon({ name: "IconCalendarEvent", source: "tabler" }, 14)}
                                                    <span><Trans>Plusieurs sessions disponibles</Trans></span>
                                                </span>
                                            )}
                                            {periodStr && (
                                                <span className="t26-event-card__meta-row">
                                                    {renderIcon({ name: "IconClock", source: "tabler" }, 14)}
                                                    <span>{periodStr}</span>
                                                </span>
                                            )}
                                            {address && (
                                                <span className="t26-event-card__meta-row">
                                                    {renderIcon({ name: "IconMapPin", source: "tabler" }, 14)}
                                                    <span>{address}</span>
                                                </span>
                                            )}
                                        </span>

                                        {localized.description && (
                                            <span style={{display:"block"}} className="t26-event-card__desc">{localized.description}</span>
                                        )}

                                        {links.length > 0 && (
                                            <span className="t26-event-card__links">
                                                {links.slice(0, 3).map((l, i) => (
                                                    <span
                                                        key={i}
                                                        role="link"
                                                        tabIndex={0}
                                                        className="t26-event-card__link"
                                                        onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); window.open(l.url, "_blank", "noopener,noreferrer"); }}
                                                    >
                                                        {renderIcon({ name: "IconExternalLink", source: "tabler" }, 12)}
                                                        {l.name || l.label || t`Lien`}
                                                    </span>
                                                ))}
                                            </span>
                                        )}

                                        <span className="t26-event-card__more">
                                            <Trans>Voir plus</Trans>
                                            <Icon name="arrowRight" size={14} className="t26-flip-rtl" />
                                        </span>
                                    </span>
                                </a>
                            </Link>
                        );
                    })}
                </div>

                <div style={{ textAlign: "center" }}>
                    <Link href={`${org.BASE_WEB_URL}/evenements`} legacyBehavior>
                        <a className="t26-section__more">
                            <Trans>Tous les événements</Trans>
                            <span className="t26-section__more-arrow">
                                <Icon name="arrowRight" size={14} className="t26-flip-rtl" />
                            </span>
                        </a>
                    </Link>
                </div>
            </section>
        </>
    );
}
